import { useSessionStorage } from "usehooks-ts";
import { BotResponseData, FilterResultsData, PreprocessingResult, ResponseClassification, TokenUsageData, UserQuestionData } from "../types/MessageData";
import { useEffect, useState } from "react";
import { ConversationState } from "../types/ConversationState";
import { backendProxy } from "./backendProxy";
import { useClientId } from "./useClientId";
import { useRequestParameter } from "./useRequestParameter";

type FinalBotResponse = {
    classification: ResponseClassification;
    message: BotResponseData;
    conversationHistory: string;
    preprocessingResult: PreprocessingResult;
    additionalRelevantTopics: string[];
    mainTopic: string;
    subTopic: string;
    tokenUsage: TokenUsageData;
    filterResults: FilterResultsData;
}


export const useConversation = (initialValue: (UserQuestionData | BotResponseData)[]) => {
    const [state, setState] = useState<ConversationState>('initializing');
    const clientId = useClientId();
    const contextRetrievalMode = useRequestParameter('crm', 'default');
    const [messages, setMessages] = useSessionStorage<(UserQuestionData | BotResponseData)[]>('conversation-history', initialValue);
    const [serializedConversationHistory, setSerializedConversationHistory] = useSessionStorage<string>('serialized-conversation-history', '');
    const [currentIncompleteResponse, setCurrentIncompleteResponse] = useState<BotResponseData |undefined>(undefined);
    
    useEffect(() => {
        if (state === 'initializing') {
            const result1 = backendProxy.warmupBackend();
            const result2 = backendProxy.trackEvent('client-initialized', clientId, { });
            Promise.all([result1, result2]).then(() => {
                setState('idle');
            })
        }
        if (state === 'response_complete') {
            setState('idle');
        }
    }, [state, clientId]);

    const addUserMessage = async (text: string, topic?: string): Promise<void> => {
        if (text && text.length > 0) {
            const now = Date.now();
            const userMessage: UserQuestionData = {
                clientId: clientId,
                messageId: now.toString(),
                role: 'user',
                content: text,
                topic: topic
            }
            setMessages([...messages, userMessage]);
            setState('sending');

            let response: BotResponseData = {
                clientId: clientId,
                classification: 'other',
                messageId: (now + 1).toString(),
                role: "assistant",
                content: '',
                completed: false,
                feedback: 'none',
                additionalRelevantTopics: [],
                mainTopic: '',
                subTopic: '',
                tokenUsage: { input: 0, output: 0, total: 0}
            };

            try {
                const responseStream = await backendProxy.postMessage(serializedConversationHistory, userMessage, response.messageId, contextRetrievalMode);
                setState('responding');

                const reader = responseStream?.getReader();
                const decoder = new TextDecoder();
                const finalResponseMarker = "|||||";
                const resetMarker = "?????";
                let finalJsonResponse = "";

                let done = false;
                while (!done && reader) {
                    const { value, done: doneReading } = await reader.read()!;
                    done = doneReading;
                    let chunk = decoder.decode(value, { stream: true });
                    if (response.content.indexOf("\n\nZweiter Versuch...\n\n") > 0) {
                        response.content = "";
                    }
                    if (chunk.indexOf(resetMarker) >= 0) {
                        chunk = chunk.replace(resetMarker, '');
                        response.content = response.content + "\n\nZweiter Versuch...\n\n"; // eslint-disable-line
                    } 
                    if (finalJsonResponse.length > 0) {
                        finalJsonResponse = finalJsonResponse + chunk;
                        chunk = "";
                    }
                    if (chunk.indexOf(finalResponseMarker) >= 0) {
                        finalJsonResponse = chunk.substring(chunk.indexOf(finalResponseMarker) + finalResponseMarker.length);
                        chunk = chunk.substring(0, chunk.indexOf(finalResponseMarker));
                    }
                    response.content += chunk;
                    setCurrentIncompleteResponse({...response});
                    if (finalJsonResponse.length > 0) {
                        try {
                            const json: FinalBotResponse = JSON.parse(finalJsonResponse);
                            if (json.conversationHistory) {
                                setSerializedConversationHistory(json.conversationHistory);
                            }
                            userMessage.preprocessingResult = json.preprocessingResult;
                            setMessages([...messages, userMessage]);
                            response.classification = json.classification || 'other';
                            response.mainTopic = json.mainTopic || '';
                            response.subTopic = json.subTopic || '';
                            response.additionalRelevantTopics = json.additionalRelevantTopics || '';
                            response.tokenUsage = json.tokenUsage;
                            response.filterResults = json.filterResults;
                            done = true;
                        } catch {
                            // no valid json response - means: not received the full json result yet 
                        }
                    } 
                }
                response.completed = true;
                setMessages((prevMessages) => [
                    ...prevMessages,
                    {...response},
                ]);
                setCurrentIncompleteResponse(undefined);
                setState('response_complete');
            } catch (e: any) {
                console.log("ERROR")
                response.completed = true;
                response.content = e.message || JSON.stringify(e); // eslint-disable-line
                setMessages((prevMessages) => [
                    ...prevMessages,
                    {...response},
                ]);
                setState('response_complete');
            }
        }
    }

    const updateMessage = (message: (UserQuestionData | BotResponseData)) => {
        const updatedMessages = messages.map(m => m.messageId === message.messageId ? message: m);
        setMessages([...updatedMessages]);
    }

    const resetConversation = async () => {
        await backendProxy.trackEvent('reset-conversation', clientId, { nofMessages: messages.length});
        setSerializedConversationHistory("");
        setMessages([]);
    }

    const allMessages = [...messages];
    if (currentIncompleteResponse) {
        allMessages.push({...currentIncompleteResponse})
    }
    return {
        state, messages: allMessages, serializedConversationHistory, addUserMessage, updateMessage, resetConversation, clientId
    }
}